/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import { useFetchUserAnalysisReport } from './Hook/Index';
import {
  Table,
  Row,
  Col,
  Card,
  Empty,
  DatePicker,
  Tabs,
  Tooltip,
  Spin,
  Tag,
  Progress,
  Alert,
  Typography,
  Button,
  Select,
  Space,
} from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  DebouncedSelect,
  fetchCustomerList,
} from '../../components/useDebouncedSelectLocation';
import { useOperatorStore } from '../../store/useOperatorStore';
import {
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import Papa from 'papaparse';
import PropTypes from 'prop-types';
import TransactionDetailsDrawer from '../../components/Modals/TransactionDetailsModal';
import { formatCurrency } from '../../utils/utils';
import ReportTrendChart from './ReportTrendChart';

const { Text } = Typography;
const { Option } = Select;

export default function UserAnalysisTab({ active = false }) {
  const { t } = useTranslation('reportPage');
  const location = useLocation();
  const { operatorId } = useOperatorStore();
  const [dateRange, setDateRange] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(
    location.state?.selectedCustomer
      ? {
          value: location.state.selectedCustomer,
          label: location.state.customerLabel || location.state.selectedCustomerName || location.state.selectedCustomer
        }
      : null
  );
  const [selectedCustomerName, setSelectedCustomerName] = useState(location.state?.selectedCustomerName || null);
  const [customerLabel, setCustomerLabel] = useState(location.state?.customerLabel || null);
  const [rfidFilter, setRfidFilter] = useState('ALL');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  // Set default date range for better UX (last 30 days)
  useEffect(() => {
    if ((active || location.state?.selectedCustomer) && !dateRange.length) {
      const endDate = dayjs();
      const startDate = dayjs().subtract(30, 'day');
      setDateRange([
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD'),
      ]);
    }
  }, [active, dateRange.length, location.state?.selectedCustomer]);

  // Set customer from navigation state if available
  useEffect(() => {
    if (location.state?.selectedCustomer) {
      setSelectedCustomer(location.state.selectedCustomer);
      setCustomerLabel(location.state.customerLabel);
      setSelectedCustomerName(location.state.selectedCustomerName);
    }
  }, [location.state]);

  const { data, isLoading, isError, error } = useFetchUserAnalysisReport(
    {
      customerId: selectedCustomer,
      fromDate: dateRange[0],
      toDate: dateRange[1],
      isRFID:
        rfidFilter === 'ALL' ? undefined : rfidFilter === 'RFID' ? true : false,
    },
    {
      enabled: !!(location.state?.selectedCustomer) || !!selectedCustomer,
      refetchOnWindowFocus: false,
    }
  );

  const handleDateChange = (dates) => {
    setDateRange(
      dates
        ? [
            dayjs(dates[0]).format('YYYY-MM-DD'),
            dayjs(dates[1]).format('YYYY-MM-DD'),
          ]
        : []
    );
  };

  const handleCustomerChange = (value, option) => {
    setSelectedCustomer(value);
    setSelectedCustomerName(option?.label || null);
    setCustomerLabel(option?.label || null);
  };

  const totalSessions =
    (data?.summary?.successSessions || 0) +
    (data?.summary?.failedSessions || 0);

  const successRate =
    totalSessions > 0
      ? Math.round((data.summary.successSessions / totalSessions) * 100)
      : 0;

  // Color-coded status display
  const getStatusTag = (status) => {
    const statusConfig = {
      Completed: { color: 'success', icon: <CheckCircleOutlined /> },
      Failed: { color: 'error', icon: <CloseCircleOutlined /> },
      InProgress: { color: 'processing', icon: <SyncOutlined spin /> },
      Interrupted: { color: 'warning', icon: <ExclamationCircleOutlined /> },
    };

    const config = statusConfig[status] || { color: 'default', icon: null };
    return (
      <Tag color={config.color} icon={config.icon} variant="outlined">
        {t(status, { defaultValue: status })}
      </Tag>
    );
  };

  const transactionColumns = [
    {
      title: t('transactionId'),
      dataIndex: 'ocppTransactionId',
      key: 'ocppTransactionId',
      render: (value) => ( <span className='text-primary cursor-pointer hover:underline' onClick={() => {
        setSelectedTransactionId(value);
        setDrawerOpen(true);
      }}>{value}</span>),
      ellipsis: true,
    },
    {
      title: t('serialNumber'),
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      responsive: ['sm'],
      ellipsis: true,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      filters: [
        { text: t('Completed'), value: 'Completed' },
        { text: t('Failed'), value: 'Failed' },
        { text: t('InProgress'), value: 'InProgress' },
        { text: t('Interrupted'), value: 'Interrupted' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: t('consumption'),
      dataIndex: 'consumption',
      key: 'consumption',
      responsive: ['md'],
      render: (value) => (
        <Tooltip title={`${value} Wh`}>{(value / 1000).toFixed(4)} kWh</Tooltip>
      ),
      sorter: (a, b) => a.consumption - b.consumption,
    },
    {
      title: t('netAmount'),
      dataIndex: 'amount',
      key: 'amount',
      responsive: ['lg'],
      render: (value) => `${formatCurrency((value || 0).toFixed(2))}`,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: t('freeTierKwh'),
      dataIndex: 'freeTierAppliedWh',
      key: 'freeTierAppliedWh',
      responsive: ['lg'],
      render: (value) =>
        value != null ? `${(Number(value) / 1000).toFixed(3)} kWh` : '-',
    },
    {
      title: t('totalCost'),
      dataIndex: 'totalCost',
      key: 'totalCost',
      responsive: ['sm'],
      render: (value) => `${formatCurrency(value.toFixed(2))}`,
      sorter: (a, b) => a.totalCost - b.totalCost,
    },
    {
      title: t('taxAmount'),
      dataIndex: 'taxAmount',
      key: 'taxAmount',
      responsive: ['lg'],
      render: (value) => `${formatCurrency(value.toFixed(2))}`,
    },
  ];

  const handleExportCsv = () => {
    if (!data?.transactions?.length) return;

    const csvData = data.transactions.map((record) => ({
      SerialNumber: record.serialNumber,
      Status: record.status ? t(record.status, { defaultValue: record.status }) : 'N/A',
      Consumption: `${(record.consumption / 1000).toFixed(4)} kWh`,
      FreeTierKwh:
        record.freeTierAppliedWh != null
          ? `${(Number(record.freeTierAppliedWh) / 1000).toFixed(3)} kWh`
          : '-',
      NetAmount: `${formatCurrency((record.amount || 0).toFixed(2))}`,
      TotalCost: `${formatCurrency(record.totalCost.toFixed(2))}`,
      VatAmount: `${formatCurrency(record.taxAmount.toFixed(2))}`,
      StartTime: record.startTime
        ? dayjs(record.startTime).format('YYYY-MM-DD HH:mm:ss')
        : '-',
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `UserAnalysis_${selectedCustomer}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRowClick = (record) => {
    setSelectedTransactionId(record._id);
    setDrawerOpen(true);
  };

  return (
    <div style={{ padding: '0 8px' }}>
      <Space orientation="vertical" style={{ width: '100%' }} size="small">
        {isError && (
          <Alert
            message={t('error')}
            description={error?.message || t('somethingWentWrong')}
            type="error"
            showIcon
            className="mb-4"
            closable
            style={{ fontSize: '14px' }}
          />
        )}

        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} sm={12} md={8}>
            <DebouncedSelect
              placeholder={t('searchCustomer')}
              fetchOptions={(search) => fetchCustomerList({ operatorId, search })}
              value={selectedCustomer}
              onChange={handleCustomerChange}
              allowClear
              className="w-full"
              size="large"
              showSearch
              filterOption={false}
              optionLabelProp="label"
              options={selectedCustomer ? [{
                value: selectedCustomer,
                label: customerLabel || selectedCustomerName || selectedCustomer
              }] : []}
              formatOptionLabel={(option) => {
                if (option.label) return option.label;
                return option.value;
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <DatePicker.RangePicker
              onChange={handleDateChange}
              className="w-full"
              size="large"
              value={
                dateRange.length === 2
                  ? [dayjs(dateRange[0]), dayjs(dateRange[1])]
                  : null
              }
              presets={[
                {
                  label: t('today'),
                  value: [dayjs(), dayjs()],
                },
                {
                  label: t('thisWeek'),
                  value: [dayjs().startOf('week'), dayjs().endOf('week')],
                },
                {
                  label: t('thisMonth'),
                  value: [dayjs().startOf('month'), dayjs().endOf('month')],
                },
                {
                  label: t('last30Days'),
                  value: [dayjs().subtract(30, 'days'), dayjs()],
                },
              ]}
            />
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Select
              value={rfidFilter}
              onChange={(value) => setRfidFilter(value)}
              className="w-full"
              size="large"
            >
              <Option value="ALL">{t('selectAll')}</Option>
              <Option value="RFID">{t('rfidOnly')}</Option>
              <Option value="NO_RFID">{t('withoutRfid')}</Option>
            </Select>
          </Col>
        </Row>

        {customerLabel && (
          <div className="my-4">
            <Alert
              message={
                <span>
                  {t('analysisFor')} <strong>{customerLabel}</strong>
                  {dateRange.length === 2 && (
                    <span className="ml-2">
                      ({dateRange[0]} - {dateRange[1]})
                    </span>
                  )}
                </span>
              }
              type="info"
              showIcon
              style={{
                marginBottom: 16,
                border: 'none',
                backgroundColor: '#d7e1dd',
                fontSize: '14px',
              }}
            />
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large" tip={t('loadingData')} />
          </div>
        ) : (
          <>
            {data?.summary && (
              <div className="mb-4">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Card 
                      size="small" 
                      title={t('successSessions')}
                      style={{ marginBottom: 16 }}
                    >
                      <div 
                        className="text-2xl text-center text-[#3f8600]"
                        style={{ fontSize: '24px', fontWeight: 'bold' }}
                      >
                        {data.summary.successSessions}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card 
                      size="small" 
                      title={t('failedSessions')}
                      style={{ marginBottom: 16 }}
                    >
                      <div 
                        className="text-2xl text-center text-[#cf1322]"
                        style={{ fontSize: '24px', fontWeight: 'bold' }}
                      >
                        {data.summary.failedSessions}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card 
                      size="small" 
                      title={t('totalConsumption')}
                      style={{ marginBottom: 16 }}
                    >
                      <div 
                        className="text-2xl text-center text-[#1677ff]"
                        style={{ fontSize: '24px', fontWeight: 'bold' }}
                      >
                        {(data.summary.totalConsumption / 1000).toFixed(4)} kWh
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card 
                      size="small" 
                      title={t('totalRevenue')}
                      style={{ marginBottom: 16 }}
                    >
                      <div 
                        className="text-2xl text-center text-[#52c41a]"
                        style={{ fontSize: '24px', fontWeight: 'bold' }}
                      >
                        {formatCurrency(data.summary.totalRevenue.toFixed(2))}
                      </div>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card
                      size="small"
                      title={t('totalFreeTierKwh')}
                      style={{ marginBottom: 16 }}
                    >
                      <div
                        className="text-2xl text-center text-[#13c2c2]"
                        style={{ fontSize: '24px', fontWeight: 'bold' }}
                      >
                        {(
                          (data.summary.totalFreeTierWh != null
                            ? Number(data.summary.totalFreeTierWh)
                            : (data.transactions || []).reduce(
                                (sum, row) =>
                                  sum + Number(row.freeTierAppliedWh || 0),
                                0,
                              )) / 1000
                        ).toFixed(3)}{' '}
                        kWh
                      </div>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {data?.summary && (
              <>
              <ReportTrendChart
                title={t('userAnalysisTrend')}
                series={(data?.series || []).map((row) => ({
                  ...row,
                  totalKwh: (row.totalWh || 0) / 1000,
                }))}
                loading={isLoading}
                fields={[
                  { key: 'successSessions', label: t('successSessions') },
                  { key: 'failedSessions', label: t('failedSessions') },
                  { key: 'totalKwh', label: t('totalEnergy') },
                ]}
              />
              <Card size="small" title={t('sessionSuccessRate')} className="mb-4 mt-4">
                <Progress
                  percent={successRate}
                  size="small"
                  status={
                    successRate > 70
                      ? 'success'
                      : successRate > 50
                        ? 'normal'
                        : 'exception'
                  }
                />
                <div className="mt-4">
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Text>
                        {t('totalSessions')}:{' '}
                        {totalSessions}
                      </Text>
                    </Col>
                    <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
                      <Text>
                        {t('successRate')}: {successRate}%
                      </Text>
                    </Col>
                  </Row>
                </div>
              </Card>
              </>
            )}

            {selectedCustomer && dateRange.length === 2 && (
              <Tabs
                className='!outline-none'
                tabBarExtraContent={{
                  right: (
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleExportCsv}
                      size="small"
                    />
                  ),
                }}
                items={[
                  {
                    key: 'transactions',
                    label: (
                      <span>
                        <CarOutlined className="mr-1" />
                        {t('transactions')}
                      </span>
                    ),
                    children: (() => {
                      if (data?.transactions?.length > 0) {
                        return (
                          <div style={{ overflowX: 'auto' }}>
                            <Table
                              columns={transactionColumns}
                              dataSource={data.transactions}
                              rowKey="_id"
                              size="small"
                              pagination={{
                                pageSize: 10,
                                showSizeChanger: false,
                                position: ['bottomCenter'],
                                size: 'default',
                              }}
                              scroll={{ x: 'max-content' }}
                              style={{ 
                                fontSize: '14px',
                                minWidth: '600px'
                              }}
                              onRow={(record) => ({
                                onClick: () => handleRowClick(record),
                                style: { cursor: 'pointer' }
                              })}
                            />
                          </div>
                        );
                      } else {
                        return (
                          <Empty
                            description={t('noTransactionsFound')}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        );
                      }
                    })(),
                  },
                ]}
              />
            )}
          </>
        )}

        <TransactionDetailsDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          id={selectedTransactionId}
        />
      </Space>
    </div>
  );
}

UserAnalysisTab.propTypes = {
  active: PropTypes.bool.isRequired,
};
